import axiosInstance from '../utils/axiosConfig';

export interface PlanProgramStore {
  id: number;
  plan_id: number;
  program_store_id: number;
  visiting_hours_override?: any;
  visit_time_minutes_override?: number;
  available_time_windows_override?: any;
  rate_override?: number;
  visiting_hours_modified: boolean;
  visit_time_minutes_modified: boolean;
  visits_count: number;
  available_time_windows_modified: boolean;
  rate_modified: boolean;
  created_at: string;
  updated_at: string;
}

export interface PlanProgramStoreDetail extends PlanProgramStore {
  original_visiting_hours?: any;
  original_visit_time_minutes?: number;
  original_available_time_windows?: any;
  original_rate?: number;
  program_name?: string;
  store_name?: string;
  store_address?: string;
}

export interface PlanWorker {
  id: number;
  plan_id: number;
  worker_id: number;
  availability_hours_override?: any;
  pricing_override?: number;
  work_time_override?: number;
  default_timings_override?: any;
  availability_hours_modified: boolean;
  pricing_modified: boolean;
  work_time_modified: boolean;
  default_timings_modified: boolean;
  created_at: string;
  updated_at: string;
}

export interface PlanWorkerDetail extends PlanWorker {
  original_availability_hours?: any;
  original_pricing?: number;
  original_work_time?: number;
  original_default_timings?: any;
  worker_name?: string;
  worker_email?: string;
}

export interface Plan {
  id: number;
  name: string;
  month: string; // YYYY-MM format  
  start_date: string;
  days: number;
  region_id?: number;
  settlement_id?: number;
  region?: {
    id: number;
    name: string;
  };
  settlement?: {
    id: number;
    name: string;
    region_id?: number;
  };
  stats?: {
    workers: {
      total: number;
      weekly_hours: number;
      monthly_hours: number;
      transport_distribution: {
        auto: number;
        public_transport: number;
        other: number;
      };
    };
    stores: {
      total: number;
      monthly_visits: number;
      monthly_visit_hours: number;
    };
    calculated_at?: string;
  };
  created_at?: string;
  updated_at?: string;
  plan_program_stores?: PlanProgramStore[];
  plan_workers?: PlanWorker[];
  calendar_planning_meta?: any;
}

export interface PlanDetail extends Plan {
  plan_program_stores: PlanProgramStoreDetail[];
  plan_workers: PlanWorkerDetail[];
}

export interface PlanWorkerCreate {
  worker_id: number;
  availability_hours_override?: any;
  pricing_override?: number;
  work_time_override?: number;
  default_timings_override?: any;
  availability_hours_modified?: boolean;
  pricing_modified?: boolean;
  work_time_modified?: boolean;
  default_timings_modified?: boolean;
}

export interface PlanCreate {
  name: string;
  month?: string; // YYYY-MM format - optional since backend doesn't require it
  start_date: string;
  days: number;
  region_id?: number;
  settlement_id?: number;
  program_ids: number[];
  plan_workers: PlanWorkerCreate[]; // Changed from worker_ids to plan_workers
}

export interface PlanUpdate {
  name?: string;
  month?: string; // Optional since backend doesn't require it
  start_date?: string;
  days?: number;
  region_id?: number;
  settlement_id?: number;
  program_ids?: number[];
  plan_workers?: PlanWorkerCreate[]; // Changed from worker_ids to plan_workers
}

export interface PlanListResponse {
  plans: Plan[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

export interface PlanProgramStoreUpdate {
  visiting_hours_override?: any;
  visit_time_minutes_override?: number;
  visits_count?: number;
  available_time_windows_override?: any;
  rate_override?: number;
  visiting_hours_modified?: boolean;
  visit_time_minutes_modified?: boolean;
  available_time_windows_modified?: boolean;
  rate_modified?: boolean;
}

export interface PlanWorkerUpdate {
  availability_hours_override?: any;
  pricing_override?: number;
  work_time_override?: number;
  default_timings_override?: any;
  availability_hours_modified?: boolean;
  pricing_modified?: boolean;
  work_time_modified?: boolean;
  default_timings_modified?: boolean;
}

export const listPlans = async (params?: { name?: string; month?: string; skip?: number; limit?: number }) => {
  const response = await axiosInstance.get<PlanListResponse>('/api/v1/plans', { params });
  return response.data.plans;
};

export const createPlan = async (plan: PlanCreate) => {
  const response = await axiosInstance.post<Plan>('/api/v1/plans', plan);
  return response.data;
};

export const getPlan = async (id: number) => {
  const response = await axiosInstance.get<PlanDetail>(`/api/v1/plans/${id}`);
  return response.data;
};

export const updatePlan = async (id: number, plan: PlanUpdate) => {
  const response = await axiosInstance.put<Plan>(`/api/v1/plans/${id}`, plan);
  return response.data;
};

export const deletePlan = async (id: number) => {
  await axiosInstance.delete(`/api/v1/plans/${id}`);
};

export const updatePlanProgramStore = async (
  planId: number,
  ppsId: number,
  update: PlanProgramStoreUpdate
) => {
  const response = await axiosInstance.put<PlanProgramStoreDetail>(
    `/api/v1/plans/${planId}/program-stores/${ppsId}`,
    update
  );
  return response.data;
};

export const updatePlanWorker = async (
  planId: number,
  pwId: number,
  update: PlanWorkerUpdate
) => {
  const response = await axiosInstance.put<PlanWorkerDetail>(
    `/api/v1/plans/${planId}/workers/${pwId}`,
    update
  );
  return response.data;
}; 

export const getPlanTimeMatrix = async (planId: number, provider: string, routeTypes?: string) => {
  const params: any = { provider };
  if (routeTypes) params.route_types = routeTypes;
  const response = await axiosInstance.get(`/api/v1/plans/${planId}/time-matrix`, { params });
  return response.data;
}; 

export const savePlanTimeMatrix = async (planId: number, provider: string, routeType: string, matrix: number[][]) => {
  const response = await axiosInstance.post(`/api/v1/plans/${planId}/time-matrix`, {
    provider,
    route_type: routeType,
    matrix,
  });
  return response.data;
};

export const savePlanTimeMatricesBulk = async (planId: number, provider: string, matrices: { [key: string]: number[][] }) => {
  const response = await axiosInstance.post(`/api/v1/plans/${planId}/time-matrix/bulk`, {
    provider,
    matrices,
  });
  return response.data;
};

export const getPlanTimeMatrixMeta = async (planId: number) => {
  const response = await axiosInstance.get(`/api/v1/plans/${planId}/time-matrix/meta`);
  return response.data;
};

export const getSavedTimeMatrices = async (planId: number, provider: string) => {
  const response = await axiosInstance.get(`/api/v1/plans/${planId}/time-matrix/saved`, {
    params: { provider }
  });
  return response.data;
};

export const exportPlanToYandexExcel = async (planId: number): Promise<Blob> => {
  const response = await axiosInstance.get(`/api/v1/plans/${planId}/export-yandex-excel`, {
    responseType: 'blob'
  });
  return response.data;
};

export const exportPlanToYandexJson = async (planId: number): Promise<any> => {
  const response = await axiosInstance.get(`/api/v1/plans/${planId}/export-yandex-json`);
  return response.data;
}; 